import { useState } from "react";
import { useOrderOverview } from "@/hooks/useOrderOverview";
import { useQueryClient } from "@tanstack/react-query";
import {
  OrderOverviewSearch,
  OrderOverviewFilters,
  OrderOverviewTable,
} from "./order-overview";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";

export const OrderOverviewList = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [orderType, setOrderType] = useState("all");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("order_code");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const pageSize = 20;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useOrderOverview({
    page,
    pageSize,
    searchTerm,
    orderType,
    status,
    sortBy,
    sortOrder,
  });

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["order-overview"] });
  };

  if (error) {
    toast({
      title: "เกิดข้อผิดพลาด",
      description: "ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง",
      variant: "destructive",
    });
  }

  const totalPages = data ? Math.ceil(data.count / pageSize) : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <OrderOverviewSearch value={searchTerm} onChange={setSearchTerm} />
        <OrderOverviewFilters
          orderType={orderType}
          status={status}
          onOrderTypeChange={setOrderType}
          onStatusChange={setStatus}
        />
      </div>

      {data && (
        <div className="text-sm text-muted-foreground">
          แสดงข้อมูล Fishsix เท่านั้น (จำนวนทั้งหมด: {data.count} รายการ)
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : (
        <>
          <OrderOverviewTable
            data={data?.data || []}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onRefresh={handleRefresh}
          />

          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={
                      page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"
                    }
                  />
                </PaginationItem>

                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => setPage(pageNum)}
                        isActive={page === pageNum}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={
                      page === totalPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </div>
  );
};
